import React, { useEffect, useState, useRef } from "react";
import OpenAI from "openai";
import { FaCopy } from "react-icons/fa";
import { toast } from "react-toastify";
import Input from "../../components/input";
import useAxiosInstance from "../../hooks/useAxios";
import Slider from "@mui/material/Slider";
import JsonView from "@uiw/react-json-view";
// import sampleReportData from "./data.json";

const PlaygroundModel = ({prompt,setPrompt, promptId, setPromptId,temperature, setTemperature,maxTokens, setMaxTokens,modelType, setModelType,topP, setTopP,frequencyPenalty, setFrequencyPenalty,presencePenalty, setPresencePenalty, engine, setEngine, systemMessage, setSystemMessage,functions, setFunctions }) => {
  // Modal States
  const [modelTypes, setModelTypes] = useState([]);
  const [numResponses, setNumResponses] = useState(1);
  const [stop, setStop] = useState(null);
  const [response, setResponse] = useState({});
  const [promptOutputLoading, setPromptOutputLoading] = useState(false);
  const [viewMode, setViewMode] = useState("simple");
  const [engines, setEngines] = useState([]);
  const [loading, setLoading] = useState(false);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [reportId, setReportId] = useState("");
  const [reportData, setReportData] = useState(null);
  const [reportLoading, setReportLoading] = useState(false);
  const [checkedItems, setCheckedItems] = useState({});

  const prevCheckedItemsRef = useRef({});
  const axios = useAxiosInstance();

  useEffect(() => {
    setLoading(true);
    axios.get("/assets/get_all_engines/").then((res) => {
      setEngines(res.data.data);
      setModelTypes(res.data.models_types);
      setLoading(false);
    });
  }, []);

  const buildTextFragment = (l1Key, l2Key) => {
    if (!reportData) return "";
    const dataToAppend = reportData[l1Key]?.[l2Key];
    if (!dataToAppend) return "";

    const content = JSON.stringify(dataToAppend, null, 2);
    return content;
  };

  useEffect(() => {
    const prevChecked = prevCheckedItemsRef.current;
    
    const allKeys = new Set([...Object.keys(prevChecked), ...Object.keys(checkedItems)]);
    allKeys.forEach(key => {
        const wasChecked = !!prevChecked[key];
        const isChecked = !!checkedItems[key];

          if (isChecked && !wasChecked) {
            const [l1Key, l2Key] = key.split(".");
            const textToAdd = buildTextFragment(l1Key, l2Key);
            setPrompt(p => p + textToAdd);
        } else if (!isChecked && wasChecked) {
            const [l1Key, l2Key] = key.split(".");
            const textToRemove = buildTextFragment(l1Key, l2Key);
            setPrompt(p => p.replace(textToRemove, ''));
        }
    });

    prevCheckedItemsRef.current = checkedItems;
  }, [checkedItems, reportData]);


  const handleFire = () => {
    setPromptOutputLoading(true);
    axios
      .post("/assets/view_fire_f_get_ai_results/", {
        prompt: prompt,
        prompt_id:promptId,
        temperature: temperature,
        max_tokens: maxTokens,
        ai_models:modelType,
        top_p: topP,
        frequency_penalty: frequencyPenalty,
        presence_penalty: presencePenalty,
        engine: engine.name,
        num_responses: numResponses,
        stop: stop,
        system_message: systemMessage,
        function: functions ? JSON.parse(functions):null,
      })
      .then((res) => {
        setResponse(res.data.data);
        setPromptOutputLoading(false);
      })
      .catch((r) => {
        setPromptOutputLoading(false);
        toast.error("Something went wrong while firing prompt");
      });
  };

  const handleFetchReport = () => {
    if (!reportId) {
      toast.warn("Please enter a Report ID to fetch data.");
      return;
    }

    setReportLoading(true);
    axios.post(`/assets/download_report/`,
      {
        "report_id": reportId,
        "file_type": "json",
        "hide_from_recent": false
      }
    ).then((res)=>{
      console.log(res.data);
      if (typeof res.data === 'object' && res.data !== null) {
        setReportData(res.data);
      } else if (typeof res.data === 'string') {
        try {
          // Clean the string by replacing NaN with null before parsing
          const cleanedData = res.data.replace(/:\s*NaN\s*([,}])/g, ': null$1');
          const jsonResponse = JSON.parse(cleanedData);
          setReportData(jsonResponse);
          console.log(jsonResponse);
        } catch (error) {
          console.error('Failed to parse JSON str ing:', error);
          toast.error('Invalid JSON format received');
        }
      }
      setCheckedItems({});
      setReportLoading(false);
      toast.success(`Successfully fetched data for Report ID: ${reportId}`);
    }).catch((r)=>{
      setReportLoading(false);
    })
    // setReportLoading(true);
    // setTimeout(() => {
    //   setPrompt("");
    //   setCheckedItems({});
    //   setReportData(sampleReportData);
    //   setReportLoading(false);
    //   toast.success(`Successfully fetched data for Report ID: ${reportId}`);
    // }, 1000);
  };

  const handleCheckboxChange = (l1Key, l2Key) => {
    const compositeKey = `${l1Key}.${l2Key}`;
    setCheckedItems((prev) => ({
      ...prev,
      [compositeKey]: !prev[compositeKey],
    }));
  };

  const handleClearAndFetchNew = () => {
      setReportData(null);
      setReportId("");
      setPrompt("");
      setCheckedItems({});
  }

  if (loading) {
    return <p className="p-6">Loading...</p>;
  }

  const TextWithBreakLine = ({ textWithNewlines }) =>
    textWithNewlines.split("\n").map((line, index) => {
      return (
        <React.Fragment key={index}>
          {line}
          <br />
        </React.Fragment>
      );
    });

  const parseJson = (response)=>{
    if (typeof response?.l1_response == "object") {
      return response;
    }
    try {
      return JSON.parse(response?.l1_response);
    } catch (error) {
      toast.info("Invalid json try shifting to simple or other view mode");
      return {};
    }
  };

  return (
    <div>
      {isModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex justify-center items-start pt-20">
          <div className="bg-white p-6 rounded-lg shadow-xl w-full max-w-3xl max-h-[80vh] flex flex-col">
            <div className="flex justify-between items-center mb-4 flex-shrink-0">
              <h2 className="text-xl font-bold">Fetch Report Data</h2>
              <button onClick={() => setIsModalOpen(false)} className="text-2xl font-bold">×</button>
            </div>
            
            {!reportData ? (
                <div>
                    <p className="text-sm font-semibold text-black mb-2">Report ID</p>
                    <div className="flex items-center gap-x-2 mb-4">
                        <Input type={"text"} value={reportId} onChange={(e) => setReportId(e.target.value)} placeholder="Enter Report ID..."/>
                        <button onClick={handleFetchReport} disabled={reportLoading} className="px-4 py-2 rounded text-sm bg-black font-medium text-white disabled:bg-gray-400 disabled:cursor-not-allowed whitespace-nowrap">
                            {reportLoading ? "Fetching..." : "Fetch Report Data"}
                        </button>
                    </div>
                </div>
            ) : (
                <div className="overflow-y-auto flex-grow">
                    <div className="flex justify-between items-center mb-3">
                        <p className="font-semibold">Editing for Report ID: {reportId}</p>
                         <button onClick={handleClearAndFetchNew} className="px-3 py-1 text-xs rounded bg-black text-white hover:bg-gray-800 cursor-pointer">
                            Clear & Fetch New
                        </button>
                    </div>
                    <div className="p-3 border rounded-lg bg-white">
                        <p className="text-md font-bold text-black mb-3">
                            Select sections to add/remove from the prompt
                        </p>
                        {Object.keys(reportData).map((l1Key) => (
                            <div key={l1Key} className="mb-4">
                            <h3 className="font-semibold text-gray-800 border-b pb-1 mb-2 text-lg">{l1Key}</h3>
                            <div className="pl-2">
                                {Object.keys(reportData[l1Key]).map((l2Key) => (
                                <div key={`${l1Key}-${l2Key}`} className="flex items-center my-2">
                                    <input type="checkbox" id={`${l1Key}.${l2Key}`} checked={!!checkedItems[`${l1Key}.${l2Key}`]} onChange={() => handleCheckboxChange(l1Key, l2Key)} className="h-4 w-4 rounded border-gray-300 cursor-pointer"/>
                                    <label htmlFor={`${l1Key}.${l2Key}`} className="ml-3 block text-sm font-medium text-gray-700 cursor-pointer">{l2Key}</label>
                                </div>
                                ))}
                            </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}
            <div className="flex justify-end mt-4 gap-x-2 flex-shrink-0">
                <button onClick={() => setIsModalOpen(false)} className="px-4 py-2 rounded text-sm bg-gray-200 hover:bg-gray-300 font-medium text-black">
                    Close
                </button>
            </div>
          </div>
        </div>
      )}

      <div className="mr-4  p-3 my-5 border rounded-lg bg-white">
        <div className="flex justify-between items-center mb-4">
            <p className="text-2xl text-black font-medium">Playground</p>
            <button onClick={() => setIsModalOpen(true)} className="px-4 py-2 rounded text-sm bg-black cursor-pointer font-medium text-white">
                Fetch/Edit Report
            </button>
        </div>
        <div className="flex gap-x-4">
          <div className="w-[70%] rounded-xl bg-gray-100 p-3">
            <p className="text-sm font-semibold text-black mb-2">
              System message
            </p>
            <div>
              <Input
                type={"text"}
                value={systemMessage}
                onChange={(e) => {
                  setSystemMessage(e.target.value);
                }}
              />
            </div>
            
            <p className="text-sm font-semibold text-black mt-4 mb-2">Prompt</p>
            <div>
              <textarea
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                className="w-full p-2 border rounded-lg bg-white focus:ring-0 focus:outline-gray-400"
                rows={15}
                placeholder="Type your prompt here or fetch content from a report..."
              />
            </div>

            <button
              disabled={promptOutputLoading || !prompt}
              onClick={()=>handleFire()}
              className="mt-3 px-4 py-2 rounded text-sm bg-black font-medium text-white disabled:bg-[#b6b6b6] disabled:cursor-not-allowed"
            >
              Fire
            </button>

            <div className="flex justify-end gap-x-2 items-center">
              <p className="text-sm font-semibold text-black mt-4 mb-2">
                View mode
              </p>
              <select
                className="border rounded-lg px-3 py-2 w-[200px] focus:ring-0 focus:outline-gray-400 "
                value={viewMode}
                onChange={(e) => {
                  setViewMode(e.target.value);
                }}
              >
                <option value={"html"}>Html</option>
                <option value={"simple"}>Simple</option>
                <option value={"json"}>Json</option>
                <option value={"raw_json"}>Raw Json</option>
              </select>
            </div>

            {promptOutputLoading ? (
              <p className="bg-white border rounded p-3 mt-3">Loading...</p>
            ) : (
              <div className="bg-white border rounded p-3 mt-3 max-h-[500px] overflow-y-auto">
                {viewMode == "html" && (
                  <div
                    dangerouslySetInnerHTML={{ __html: response?.l1_response }}
                  />
                )}
                {viewMode == "raw_json" && (
                  <JsonView
                  shortenTextAfterLength={0}
                  value={response}
                  displayObjectSize={false}
                  displayDataTypes={false}
                />
                )}
                {viewMode == "simple" && <p>{JSON.stringify(response?.l1_response)}</p>}
                {viewMode == "json" && (
                  <JsonView
                    shortenTextAfterLength={0}
                    value={parseJson(response)}
                    displayObjectSize={false}
                    displayDataTypes={false}
                  />
                )}
              </div>
            )}
          </div>


          <div className="w-[30%] rounded-xl bg-gray-100  p-3">
          <p className="text-sm font-semibold mb-2 ">Model Type</p>
            <select
              value={modelType}
              onChange={(e) => {
                setModelType(e.target.value);
              }}
              className="border rounded-lg px-3 py-2 w-full focus:ring-0 focus:outline-gray-400 "
            >
              {[
                { name: "choose", pk: -1 },
                ...modelTypes,
              ].map((engine) => {
                return (
                  <option key={engine.pk} value={engine.name}>
                    {engine.name}
                  </option>
                );
              })}
            </select>
            <p className="text-sm font-semibold mb-2 ">Model</p>
            <select
              value={engine.name}
              onChange={(e) => {
                setEngine(engines.find((it) => it.name === e.target.value));
              }}
              className="border rounded-lg px-3 py-2 w-full focus:ring-0 focus:outline-gray-400 "
            >
              {[
                { name: "choose", pk: -1 },
                ...engines,
              ].map((engine) => {
                return (
                  <option key={engine.pk} value={engine.name}>
                    {engine.name}
                  </option>
                );
              })}
            </select>

            <p className="text-sm w-full font-semibold mb-2 mt-4 flex justify-between">
              <span>Temperature</span> <span>{temperature}</span>
            </p>
            <div className="px-3">
              <Slider
                color="info"
                min={0}
                step={0.05}
                max={1}
                value={temperature}
                onChange={(event, newValue) => {
                  setTemperature(Number(newValue));
                }}
              />
            </div>

            <p className="text-sm w-full font-semibold mb-2 mt-4 flex justify-between">
              <span>Max Token</span>
            </p>
            <Input
              color="info"
              value={maxTokens}
              type={"number"}
              onChange={(event) => {
                setMaxTokens(Number(event.target.value));
              }}
            />
            <p className="text-sm w-full font-semibold mb-2 mt-4 flex justify-between">
              <span>Prompt ID</span>
            </p>
            <Input
              color="info"
              value={promptId}
              type={"number"}
              onChange={(event) => {
                setPromptId(Number(event.target.value));
              }}
            />

            <p className="text-sm w-full font-semibold mb-2 mt-4 flex justify-between">
              <span>Function(valid json)</span>
            </p>
            <Input
              color="info"
              value={functions}
              onChange={(event) => {
                setFunctions(event.target.value);
              }}
            />

            <p className="text-sm w-full font-semibold mb-2 mt-4 flex justify-between">
              <span>Top P</span> <span>{topP}</span>
            </p>
            <div className="px-3">
              <Slider
                color="info"
                max={1}
                min={0}
                step={0.05}
                value={topP}
                type={"number"}
                onChange={(event, num) => {
                  setTopP(Number(num));
                }}
              />
            </div>

            <p className="text-sm w-full font-semibold mb-2 mt-4 flex justify-between">
              <span>Frequency Penalty</span> <span>{frequencyPenalty}</span>
            </p>
            <div className="px-3">
              <Slider
                color="info"
                max={1}
                min={0}
                step={0.05}
                value={frequencyPenalty}
                type={"number"}
                onChange={(event, num) => {
                  setFrequencyPenalty(Number(num));
                }}
              />
            </div>

            <p className="text-sm w-full font-semibold mb-2 mt-4 flex justify-between">
              <span>Presence Penalty</span> <span>{presencePenalty}</span>
            </p>
            <div className="px-3">
              <Slider
                color="info"
                max={1}
                min={0}
                step={0.05}
                value={presencePenalty}
                type={"number"}
                onChange={(event, num) => {
                  setPresencePenalty(Number(num));
                }}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PlaygroundModel;
